/**
 * Carriages (รถม้า, see docs/CARRIAGE.md): a carriage station (ท่ารถม้า) in every town and school
 * compound, each with a station master, a parked coach with its horse, and a signpost. A rider pays
 * gold by the distance and is carried to any other station they have already reached on foot.
 * Rules are in `carriage.ts`; this file is only the data.
 */
import { plains256, START, ZONES, type ZoneId } from './zones';

/** A spot of a station, in tiles from the zone's plaza (like a school master's `post`). */
export interface StationSpot {
  readonly dx: number;
  readonly dy: number;
}

export interface StationDef {
  readonly zone: ZoneId;
  /** The station's own name, on its signpost. */
  readonly name: string;
  /** Where the coach stands (its middle, between the wheels). */
  readonly coach: StationSpot;
  /** Which way the horse faces on screen at the default camera: 1 right, −1 left. */
  readonly facing: 1 | -1;
  /** Where riders board, and where they step down on arriving (the door, on the road side). */
  readonly door: StationSpot;
  /** The signpost with the hitching rail beside it. */
  readonly sign: StationSpot;
  readonly master: StationSpot & { readonly name: string; readonly title: string; readonly sprite: string };
  /**
   * Ground cleared of trees and boulders round the station, in tiles from the plaza: [dx0, dx1) ×
   * [dy0, dy1). Buildings, water and fields are never touched.
   */
  readonly clear: readonly [number, number, number, number];
  /** A placement still to be settled (shown in the report, not in the game). */
  readonly todo?: string;
}

/** Tuning. Gold is ตำลึง; seconds are world time. */
export const CARRIAGE = {
  /** Fare: a flat hire plus so much per stage of road (one stage = one step on the world grid). */
  baseFare: 10,
  farePerStage: 14,
  /** Li (ลี้) of road per stage, for the destination list. */
  liPerStage: 30,
  /** The ride sequence: so many seconds, plus so many per stage. */
  rideBase: 4,
  ridePerStage: 1.5,
  /** Climbing in and stepping down. */
  boardSeconds: 1.4,
  alightSeconds: 1.2,
  /** The ride can be skipped once it has run this long. */
  skipAfter: 1.5,
  /** How near the coach or the station master a rider must stand to hire it (tiles). */
  reach: 4.5,
  /** Seats behind the coachman: the rider and the whole party (PARTY.maxSize) fit. */
  seats: 5,
  /**
   * Where each seat is in the coach, in tiles from its middle (between the wheels): `f` toward the
   * horse, `l` to the coach's right. The first is the hirer's; the cabin's benches face each other.
   */
  seatSpots: [
    { f: 0.33, l: -0.3 },
    { f: 0.33, l: 0.3 },
    { f: -0.33, l: -0.3 },
    { f: -0.33, l: 0.3 },
    { f: -0.7, l: 0 },
  ] as readonly { readonly f: number; readonly l: number }[],
  /** Where the horse stands, in tiles ahead of the coach's middle, and its nose. */
  horseAhead: 2.55,
  noseAhead: 4.55,
  /**
   * Setting off: the coach rolls out of the station along the road, speeding up by `accel` (tiles/s²)
   * to a trot of `speed` (tiles/s), and draws up gently within `pullOut` tiles (or short of anything
   * in its way) while the ride itself plays on.
   */
  speed: 2.6,
  accel: 1.6,
  pullOut: 5,
  /** The horse's walking speed (tiles/s): below it the horse walks, above it trots. */
  walkSpeed: 1.1,
  /**
   * On a region's roads (OPEN_WORLD.md §2.4): the coach drives the road between its stations at
   * `roadSpeed` (m/s). Fares there go by the road's length: `baseFare` plus `farePerKm`, and the
   * destination list shows `liPerKm` li (ลี้) per kilometre.
   */
  roadSpeed: 11,
  farePerKm: 20,
  liPerKm: 2,
  /** How near (m) a stop's coach or master a rider must come on foot to unlock it. */
  discoverReach: 12,
  /** Heavy goods (logs, ore) a coach will not carry past this many. */
  heavyGoods: ['pine-log', 'hard-wood', 'iron-ore', 'black-iron'],
  heavyLimit: 40,
  /** Equipment the station masters refuse to carry (none yet; e.g. a cursed blade). */
  forbiddenItems: [] as readonly string[],
} as const;

/**
 * The stations. The hub and the school compounds share one layout: on the east road, its south
 * side, a little past the plaza (open ground the generator keeps clear of buildings).
 */
const ROADSIDE = {
  coach: { dx: 10.2, dy: 2.7 },
  facing: 1 as const,
  door: { dx: 9.6, dy: 1.1 },
  sign: { dx: 7.2, dy: 3.6 },
  clear: [6, 14, 1, 5] as const,
};

const master = (name: string, title = 'นายท่ารถม้า') => ({
  dx: 7.6,
  dy: 2.1,
  name,
  title,
  sprite: 'stationMaster',
});

const list: StationDef[] = [
  {
    zone: START,
    name: 'ท่ารถม้าหยานหยู',
    // The coach yard on the south road below the plaza (docs/archive/YANYU.md A5): the coach at (306, 413), ลุงเฉิน at (304.5, 410).
    coach: { dx: 6, dy: 113 },
    facing: 1,
    door: { dx: 5.5, dy: 111.5 },
    sign: { dx: 2, dy: 111 },
    master: { dx: 4.5, dy: 110, name: 'ลุงเฉิน', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
    clear: [0, 14, 106, 118],
  },
  {
    // The old village's yard (kept for the old map and its region): south-east of the drum tower, the stable along
    // its north side: the coach at (93, 93.5), ลุงเฉิน at (88.5, 89.5).
    zone: 'yanyu',
    name: 'ท่ารถม้าหยานหยู',
    coach: { dx: 12.5, dy: 19 },
    facing: 1,
    door: { dx: 11.9, dy: 17.5 },
    sign: { dx: 7, dy: 18 },
    master: { dx: 8, dy: 15, name: 'ลุงเฉิน', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
    clear: [5, 18, 15, 22],
  },
  {
    zone: 'plains',
    name: 'ท่ารถม้าลานศิลา',
    ...ROADSIDE,
    master: master('ซือหม่าเหยียน', 'นายสถานีรถม้าหลวง'),
  },
  { zone: 'mist-peak', name: 'ท่ารถม้าเชิงเขายอดหมอก', ...ROADSIDE, master: master('อาเฉิง') },
  { zone: 'bell-stone', name: 'ท่ารถม้าเขาพันบันได', ...ROADSIDE, master: master('เหล่าหวัง') },
  { zone: 'lantern-road', name: 'ท่ารถม้าตะเกียงเหล็ก', ...ROADSIDE, master: master('จางเปียว') },
  { zone: 'dim-moon', name: 'ท่ารถม้าหุบจันทร์มัว', ...ROADSIDE, master: master('ป้าหลิน') },
  { zone: 'pine-ink', name: 'ท่ารถม้าหมู่ตึก', ...ROADSIDE, master: master('ซูเหวิน') },
  { zone: 'cinnabar', name: 'ท่ารถม้าผาดาบชาด', ...ROADSIDE, master: master('เถี่ยหนิว') },
];

/**
 * The plains station on the 256 m map (PLAINS256, data/layouts/plains.ts): in the carriage yard south-west of the
 * plaza, (110, 178), the coach east of the stable facing the south road. Offsets from the plaza (128.5, 128.5).
 */
const BIG_PLAINS: Pick<StationDef, 'coach' | 'door' | 'sign' | 'clear'> & { readonly masterAt: StationSpot } = {
  coach: { dx: -14, dy: 49.5 },
  door: { dx: -14.6, dy: 47.9 },
  sign: { dx: -17, dy: 50.6 },
  masterAt: { dx: -16.6, dy: 48.6 },
  clear: [-27, -9, 43, 56],
};
const plainsBase = list.find((s) => s.zone === 'plains')!;
const plainsBig: StationDef = {
  ...plainsBase,
  coach: BIG_PLAINS.coach,
  door: BIG_PLAINS.door,
  sign: BIG_PLAINS.sign,
  clear: BIG_PLAINS.clear,
  master: { ...plainsBase.master, ...BIG_PLAINS.masterAt },
};

/** Stations by zone; the plains' follows the map in play (the 72 m or the 256 m one). */
export const STATIONS: Readonly<Partial<Record<ZoneId, StationDef>>> = Object.defineProperty(
  Object.fromEntries(list.map((s) => [s.zone, s])),
  'plains',
  { enumerable: true, get: () => (plains256() ? plainsBig : plainsBase) },
);
export const STATION_LIST: readonly StationDef[] = list;
export const hasStation = (z: ZoneId): boolean => !!STATIONS[z];

/** Stages of road between two zones: steps on the world grid, east–west plus north–south. */
export function stagesBetween(a: ZoneId, b: ZoneId): number {
  const [ax, ay] = ZONES[a].at;
  const [bx, by] = ZONES[b].at;
  return Math.abs(ax - bx) + Math.abs(ay - by);
}

/** The fare in gold (ตำลึง) from one station to another. */
export const fareBetween = (a: ZoneId, b: ZoneId): number =>
  CARRIAGE.baseFare + CARRIAGE.farePerStage * stagesBetween(a, b);

/** Seconds the ride sequence lasts from one station to another. */
export const rideSeconds = (a: ZoneId, b: ZoneId): number =>
  CARRIAGE.rideBase + CARRIAGE.ridePerStage * stagesBetween(a, b);

/** Li of road from one station to another. */
export const liBetween = (a: ZoneId, b: ZoneId): number => CARRIAGE.liPerStage * stagesBetween(a, b);

// ───────────────────────── stops on a region's roads ─────────────────────────

/**
 * A carriage stop on a region's roads (OPEN_WORLD.md §2.2, the sketch's `stations`): the coach drives
 * the road between them (`carriage.ts`, `region/roads.ts`). Only on a region map (`setRegionMaps`).
 */
export type RoadStopDef = {
  /** The sketch's station id (`data/regions/<region>.ts`), which also names its node in the road graph. */
  readonly id: string;
  /** The region (`ZoneDef.region`). */
  readonly region: string;
  /** The stop's own name, on its signpost. */
  readonly name: string;
} & (
  | {
      /** The village's own station (`STATIONS[zone]`, laid from its plaza). Its id is the zone's. */
      readonly village: true;
    }
  | {
      readonly village?: undefined;
      /** Where the coach stands (metres in the region); default the sketch's station spot. */
      readonly at?: { readonly x: number; readonly y: number };
      /** Which way the horse faces: 1 east, −1 west. */
      readonly facing: 1 | -1;
      /** The door, the signpost and the master, in metres from the coach (east, south). */
      readonly door: StationSpot;
      readonly sign: StationSpot;
      readonly master: StationSpot & {
        readonly name: string;
        readonly title: string;
        readonly sprite: string;
      };
      readonly todo?: string;
    }
);

/** A roadside stop facing west (toward a road on its west): door, sign and master mirrored from the village's. */
const WESTWARD = {
  facing: -1 as const,
  door: { dx: 0.6, dy: -1.5 },
  sign: { dx: 5.5, dy: -1 },
};

/**
 * Phase 1 (OPEN_WORLD.md §7, lane E): the village → the waystation → the town's site. The others
 * (the foot village, the lake town, ด่านใต้) join as their places are built.
 */
export const ROAD_STOPS: readonly RoadStopDef[] = [
  {
    id: 'yanyu',
    region: 'yanyu',
    name: 'ท่ารถม้าหยานหยู',
    village: true,
  },
  {
    id: 'waystation',
    region: 'yanyu',
    name: 'ท่ารถม้าศาลาพักม้าสิบลี้',
    // Between the hall and the stable, the south road on its west.
    ...WESTWARD,
    master: { dx: 4.5, dy: -4, name: 'ลุงหม่า', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
  },
  {
    id: 'hub',
    region: 'yanyu',
    name: 'ท่ารถม้าลานศิลา',
    // The city's coach yard inside the north gate, east of ถนนหลวง (docs/archive/HUB.md option ก): the
    // sketch's station spot (1494, 1512), the stable on its east.
    ...WESTWARD,
    master: { dx: 4.5, dy: -4, name: 'ซือหม่าเฉิง', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
  },
  // แคว้นเก้ากระบี่ (data/regions/jiuzhou.ts): the same road, mirrored, from the new Yanyu's own station (the zone's) to the
  // waystation and the hub. Their ids are prefixed: a stop's id is global.
  { id: 'yanyu2', region: 'jiuzhou', name: 'ท่ารถม้าหยานหยู', village: true },
  {
    id: 'jz-waystation',
    region: 'jiuzhou',
    name: 'ท่ารถม้าศาลาพักม้าสิบลี้',
    ...WESTWARD,
    master: { dx: 4.5, dy: -4, name: 'ลุงหม่า', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
  },
  {
    id: 'jz-hub',
    region: 'jiuzhou',
    name: 'ท่ารถม้าลานศิลา',
    ...WESTWARD,
    master: { dx: 4.5, dy: -4, name: 'ซือหม่าเฉิง', title: 'นายท่ารถม้า', sprite: 'stationMaster' },
  },
];

/** The stops of a region, in data order. */
export const roadStopsOf = (region: string): readonly RoadStopDef[] =>
  ROAD_STOPS.filter((s) => s.region === region);

/** The fare in gold (ตำลึง) for so many metres of road. */
export const roadFare = (metres: number): number =>
  CARRIAGE.baseFare + Math.round((CARRIAGE.farePerKm * metres) / 1000);

/** Li (ลี้) for so many metres of road. */
export const roadLi = (metres: number): number => Math.max(1, Math.round((CARRIAGE.liPerKm * metres) / 1000));

/** Seconds the coach takes over so many metres of road. */
export const roadSeconds = (metres: number): number => metres / CARRIAGE.roadSpeed;
